/**
 * Catalogue FERMÉ des services intégrables par le bloc « Contenu externe ».
 *
 * Règle du chantier (docs/webviewer/chantier-embed.md §1) : une URL qui ne
 * correspond à aucune entrée n'est JAMAIS montée dans un iframe — le « bloc
 * HTML brut » a été écarté de la palette pour risque d'injection, cette box ne
 * doit pas le réintroduire par la bande.
 *
 * Module pur, sans React : l'UI traduit une raison de refus en message, elle
 * n'inspecte jamais l'URL elle-même.
 */

/** Raisons de refus, traduites par le composant en message d'édition. */
export const EMBED_REFUSAL = {
  empty: 'empty',
  invalidUrl: 'invalid-url',
  videoBlock: 'video-block',
  unsupported: 'unsupported',
  unsupportedPath: 'unsupported-path',
  shortLink: 'short-link',
};

/** Hôte déclaré aux services qui exigent le domaine intégrateur. */
const DEFAULT_EMBED_HOST = 'smartproposal';

/**
 * `allow` / `sandbox` communs. `allow-same-origin` porte sur l'origine du
 * service (jamais la nôtre) : sans lui, ni Calendly ni Figma ne chargent.
 */
const BASE_SANDBOX = 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox';
const FORM_SANDBOX = `${BASE_SANDBOX} allow-forms`;

/**
 * Le hostname appartient-il au domaine ? Égalité stricte ou sous-domaine.
 * Une expression régulière sur l'URL entière accepterait
 * `calendly.com.evil.tld` et `notcalendly.com` : c'est le seul point réellement
 * risqué du bloc, d'où le passage par le hostname parsé.
 */
function hostMatches(hostname, domain) {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

/** Vidéos : refusées ici, le bloc Vidéo les gère (fichier + YouTube/Vimeo). */
const VIDEO_DOMAINS = ['youtube.com', 'youtu.be', 'youtube-nocookie.com', 'vimeo.com'];

/**
 * Raccourcisseurs de services par ailleurs pris en charge.
 *
 * Un `maps.app.goo.gl/…` désigne bien une carte Google, mais son domaine n'est
 * pas celui du service et sa cible ne se connaît qu'en suivant la redirection —
 * or la résolution est volontairement synchrone et hors ligne. Sans cette
 * entrée, l'utilisateur lisait « ce service n'est pas pris en charge » alors
 * que le service, lui, l'est : c'est la forme du lien qui ne va pas.
 */
const SHORT_LINK_HOSTS = {
  'maps.app.goo.gl': 'Google Maps',
  'goo.gl': 'Google Maps',
  'g.co': 'Google Maps',
};

/**
 * Un service du catalogue.
 * - `hosts` / `matchesHost` : reconnaissance du domaine ;
 * - `toIframeSrc(url, opts)` : URL publique → URL d'intégration, `null` si le
 *   chemin ne désigne pas un contenu intégrable (accueil du service, etc.) ;
 * - `defaultSize` : gabarit appliqué tant que l'utilisateur ne reprend pas la
 *   main (mode « celle du service »).
 */
export const EMBED_SERVICES = [
  {
    id: 'calendly',
    label: 'Calendly',
    hosts: ['calendly.com'],
    sandbox: FORM_SANDBOX,
    defaultSize: { mode: 'height', heightPx: 700 },
    toIframeSrc(url, { embedHost = DEFAULT_EMBED_HOST } = {}) {
      if (url.pathname === '/' || url.pathname === '') return null;
      url.searchParams.set('embed_type', 'Inline');
      url.searchParams.set('embed_domain', embedHost);
      return url.toString();
    },
  },
  {
    id: 'loom',
    label: 'Loom',
    hosts: ['loom.com'],
    sandbox: BASE_SANDBOX,
    defaultSize: { mode: 'ratio', ratio: 16 / 9 },
    toIframeSrc(url) {
      const match = url.pathname.match(/^\/(?:share|embed)\/([a-zA-Z0-9]+)/);
      if (!match) return null;
      return `https://www.loom.com/embed/${match[1]}`;
    },
  },
  {
    id: 'figma',
    label: 'Figma',
    hosts: ['figma.com'],
    sandbox: BASE_SANDBOX,
    defaultSize: { mode: 'ratio', ratio: 16 / 9 },
    toIframeSrc(url, { embedHost = DEFAULT_EMBED_HOST } = {}) {
      if (!/^\/(file|design|proto|board|slides|deck)\//.test(url.pathname)) return null;
      const embed = new URL(url.toString());
      embed.hostname = 'embed.figma.com';
      embed.searchParams.set('embed-host', embedHost);
      return embed.toString();
    },
  },
  {
    id: 'google-slides',
    label: 'Google Slides',
    hosts: ['docs.google.com'],
    sandbox: BASE_SANDBOX,
    defaultSize: { mode: 'ratio', ratio: 16 / 9 },
    toIframeSrc(url) {
      // `/presentation/d/<id>/…` et les présentations publiées `/d/e/<id>/…`.
      const match = url.pathname.match(/^\/presentation\/d\/(e\/)?([a-zA-Z0-9_-]+)/);
      if (!match) return null;
      const id = `${match[1] || ''}${match[2]}`;
      return `https://docs.google.com/presentation/d/${id}/embed?start=false&loop=false&delayms=3000`;
    },
  },
  {
    id: 'google-maps',
    label: 'Google Maps',
    // google.fr/maps, google.co.uk/maps… : le domaine varie avec le pays.
    matchesHost: (hostname) => /(^|\.)google\.[a-z]{2,3}(\.[a-z]{2})?$/.test(hostname),
    sandbox: BASE_SANDBOX,
    defaultSize: { mode: 'ratio', ratio: 4 / 3 },
    toIframeSrc(url) {
      if (!url.pathname.startsWith('/maps')) return null;
      // URL copiée depuis « Partager → Intégrer une carte » : déjà bonne.
      if (url.pathname.startsWith('/maps/embed')) return url.toString();
      url.searchParams.set('output', 'embed');
      return url.toString();
    },
  },
  {
    id: 'typeform',
    label: 'Typeform',
    hosts: ['typeform.com'],
    sandbox: FORM_SANDBOX,
    defaultSize: { mode: 'height', heightPx: 600 },
    toIframeSrc(url) {
      const match = url.pathname.match(/^\/to\/([a-zA-Z0-9]+)/);
      if (!match) return null;
      return `https://form.typeform.com/to/${match[1]}`;
    },
  },
  {
    id: 'tally',
    label: 'Tally',
    hosts: ['tally.so'],
    sandbox: FORM_SANDBOX,
    defaultSize: { mode: 'height', heightPx: 600 },
    toIframeSrc(url) {
      const match = url.pathname.match(/^\/(?:r|embed)\/([a-zA-Z0-9]+)/);
      if (!match) return null;
      return `https://tally.so/embed/${match[1]}`;
    },
  },
];

/** Libellés des services acceptés, pour les messages d'aide. */
export const EMBED_SERVICE_LABELS = EMBED_SERVICES.map((s) => s.label);

export function getEmbedService(id) {
  return EMBED_SERVICES.find((service) => service.id === id) || null;
}

function findService(hostname) {
  return EMBED_SERVICES.find((service) => (
    service.matchesHost
      ? service.matchesHost(hostname)
      : service.hosts.some((domain) => hostMatches(hostname, domain))
  )) || null;
}

/**
 * Le `src` d'un `<iframe>` collé tel quel, ou l'entrée inchangée.
 *
 * Google Maps, dans « Partager → Intégrer une carte », ne propose **que** du
 * HTML : son unique bouton est « Copier le contenu HTML ». Exiger une URL nue
 * revenait à demander l'impossible.
 *
 * **Ce n'est pas une réintroduction du bloc HTML brut.** On lit un attribut et
 * on jette le reste : ni le balisage, ni les autres attributs, ni un éventuel
 * gestionnaire d'événement ne sont conservés. L'URL extraite subit ensuite
 * exactement les mêmes contrôles qu'une URL tapée à la main — schéma `https:`,
 * hôte reconnu dans le catalogue fermé. Coller un `<iframe>` d'un service non
 * répertorié échoue donc comme avant.
 */
export function extractIframeSrc(input) {
  if (typeof input !== 'string') return input;
  if (!input.includes('<iframe')) return input;
  const match = input.match(/<iframe[^>]*\ssrc\s*=\s*["']([^"']+)["']/i);
  if (!match) return input;
  // Un attribut HTML peut porter `&amp;` là où l'URL veut `&`.
  return match[1].replace(/&amp;/gi, '&').trim();
}

/**
 * URL saisie → intégration résolue, ou refus typé.
 * @returns {{ok: true, serviceId, label, iframeSrc, sandbox, defaultSize}
 *          |{ok: false, reason: string, label?: string}}
 */
export function resolveEmbed(rawUrl, options = {}) {
  if (typeof rawUrl !== 'string' || !rawUrl.trim()) {
    return { ok: false, reason: EMBED_REFUSAL.empty };
  }

  const candidate = extractIframeSrc(rawUrl.trim());

  let url;
  try {
    url = new URL(candidate);
  } catch {
    return { ok: false, reason: EMBED_REFUSAL.invalidUrl };
  }
  // Tout sauf https : `http:` (contenu mixte bloqué par le navigateur),
  // `javascript:` et `data:` (exécution dans la page).
  if (url.protocol !== 'https:') {
    return { ok: false, reason: EMBED_REFUSAL.invalidUrl };
  }

  if (VIDEO_DOMAINS.some((domain) => hostMatches(url.hostname, domain))) {
    return { ok: false, reason: EMBED_REFUSAL.videoBlock };
  }

  const shortLinkOf = Object.keys(SHORT_LINK_HOSTS)
    .find((host) => hostMatches(url.hostname, host));
  if (shortLinkOf) {
    return {
      ok: false,
      reason: EMBED_REFUSAL.shortLink,
      label: SHORT_LINK_HOSTS[shortLinkOf],
    };
  }

  const service = findService(url.hostname);
  if (!service) return { ok: false, reason: EMBED_REFUSAL.unsupported };

  const iframeSrc = service.toIframeSrc(url, options);
  if (!iframeSrc) {
    return { ok: false, reason: EMBED_REFUSAL.unsupportedPath, label: service.label };
  }

  return {
    ok: true,
    serviceId: service.id,
    label: service.label,
    iframeSrc,
    sandbox: service.sandbox,
    defaultSize: service.defaultSize,
  };
}
